// utils/secureStorage.js
// Secure storage utilities for sensitive data like private keys

/**
 * Derives an encryption key from a password using PBKDF2
 */
async function deriveKey(password, salt) {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts a string value using AES-GCM
 * @param {string} value - The value to encrypt
 * @param {string} password - Optional password for additional encryption
 * @returns {Promise<string>} - Base64 encoded encrypted data
 */
export async function encryptValue(value, password = '') {
  try {
    // Generate a random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Use a combination of a static key and optional password
    const combinedPassword = `govstr-secure-${password}-${navigator.userAgent.slice(0, 50)}`;
    const key = await deriveKey(combinedPassword, salt);
    
    // Encrypt the value
    const encoder = new TextEncoder();
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encoder.encode(value)
    );
    
    // Combine salt + iv + encrypted data
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);
    
    // Return as base64
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt value');
  }
}

/**
 * Decrypts a previously encrypted value
 * @param {string} encryptedValue - Base64 encoded encrypted data
 * @param {string} password - The password used during encryption
 * @returns {Promise<string>} - The decrypted value
 */
export async function decryptValue(encryptedValue, password = '') {
  try {
    // Decode base64
    const combined = Uint8Array.from(atob(encryptedValue), c => c.charCodeAt(0));
    
    // Extract salt, iv, and encrypted data
    const salt = combined.slice(0, 16);
    const iv = combined.slice(16, 28);
    const encrypted = combined.slice(28);
    
    // Derive the same key
    const combinedPassword = `govstr-secure-${password}-${navigator.userAgent.slice(0, 50)}`;
    const key = await deriveKey(combinedPassword, salt);
    
    // Decrypt
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encrypted
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw new Error('Failed to decrypt value');
  }
}

/**
 * Securely stores a value in localStorage with encryption
 * @param {string} key - The storage key
 * @param {string} value - The value to store
 * @param {string} password - Optional encryption password
 */
export async function secureStore(key, value, password = '') {
  const encrypted = await encryptValue(value, password);
  localStorage.setItem(key, encrypted);
}

/**
 * Retrieves and decrypts a value from localStorage
 * @param {string} key - The storage key
 * @param {string} password - The encryption password
 * @returns {Promise<string|null>} - The decrypted value or null
 */
export async function secureRetrieve(key, password = '') {
  const encrypted = localStorage.getItem(key);
  if (!encrypted) return null;
  
  try {
    return await decryptValue(encrypted, password);
  } catch {
    // If decryption fails, the data might be in old unencrypted format
    // Try to migrate it
    console.warn('Failed to decrypt stored value, attempting migration');
    return null;
  }
}

/**
 * Securely removes a value from storage
 * @param {string} key - The storage key
 */
export function secureRemove(key) {
  localStorage.removeItem(key);
}

/**
 * Clears all sensitive data from storage
 */
export function clearAllSecureData() {
  // Remove private key
  localStorage.removeItem('privateKey');
  localStorage.removeItem('privateKey_encrypted');
  
  // Clear any cached user data
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('user-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Validates that a string looks like a valid hex private key
 * @param {string} key - The key to validate
 * @returns {boolean} - True if valid format
 */
export function isValidPrivateKeyFormat(key) {
  if (!key || typeof key !== 'string') return false;
  // Private keys should be 64 hex characters (32 bytes)
  return /^[0-9a-fA-F]{64}$/.test(key);
}

/**
 * Generates a cryptographically secure random ID
 * @returns {string} - Random hex string
 */
export function generateSecureId() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Named exports are preferred for tree-shaking
// All functions are already exported individually above

